import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { V_URL } from "../../../../../BaseUrl";
import axios from "axios";
import toast from "react-hot-toast";
import Footer from "../../../Include/Footer";
import Header from "../../../Include/Header";
import Sidebar from "../../../Include/Sidebar";
import Loader from "../../../Include/Loader";
import { Pagination } from "../../../Table";
import moment from "moment";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import { getAdminItem } from "../../../../../Store/Store/Item/AdminItem";
import { getProject } from "../../../../../Store/Store/Project/Project";
import { getCategory } from "../../../../../Store/Store/StoreMaster/Category/Category";
import { getStockAdjustment } from "../../../../../Store/prosafe/proTransaction/StockAdjustment/getStockAdjustment";
import { getStatusLabel } from "../../components/ProStatus/StatusFile";
import useDebounce from "../../../../../hooks/useDebounce";

const StockAdjustmentList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [limit, setlimit] = useState(10);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    item: null,
    category: null,
    project: null,
    status: null,
    adjustment_type: null,
  });

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    dispatch(getAdminItem({ is_main: true }));
    dispatch(getProject());
    dispatch(getCategory());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [currentPage, limit, debouncedSearch, filter]);

  const fetchData = () => {
    const payload = {
      firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
      year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
      project_id: filter.project,
      item_id: filter.item,
      category_id: filter.category,
      status: filter.status,
      adjustment_type: filter.adjustment_type,
      start_date: filter.startDate,
      end_date: filter.endDate,
      search: debouncedSearch,
      page: currentPage,
      limit: limit,
    };

    dispatch(getStockAdjustment({ formData: payload }));
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setFilter({
      startDate: null,
      endDate: null,
      item: null,
      category: null,
      project: null,
      status: null,
      adjustment_type: null,
    });
    setSearch("");
    setCurrentPage(1);
  };

  const entityResponse = useSelector(
    (state) => state.getStockAdjustment?.user?.data
  );
  const isLoading = useSelector((state) => state.getStockAdjustment?.loading);
  const itemData = useSelector((state) => state.getAdminItem?.user?.data);
  const projectData = useSelector((state) => state.getProject?.user?.data);
  const categoryData = useSelector((state) => state.getCategory?.user?.data);

  const adjustmentsData = entityResponse?.data || [];

  useEffect(() => {
    if (entityResponse?.pagination?.total !== undefined) {
      setTotalItems(entityResponse.pagination.total);
    } else if (Array.isArray(entityResponse)) {
      setTotalItems(entityResponse.length);
    }
  }, [entityResponse]);

  const handleApproveReject = async (id, voucherNo, newStatus) => {
    const isApprove = newStatus === 3;
    const actionTitle = isApprove
      ? "Approve Stock Adjustment"
      : "Reject Stock Adjustment";
    const confirmColor = isApprove ? "#28a745" : "#d33";

    let rejReason = "";
    if (!isApprove) {
      const { value: reason } = await Swal.fire({
        title: actionTitle,
        input: "textarea",
        inputLabel: "Rejection Reason",
        inputPlaceholder: "Enter why this stock adjustment is rejected...",
        inputAttributes: { "aria-label": "Type your reason here" },
        showCancelButton: true,
        confirmButtonColor: confirmColor,
        confirmButtonText: "Reject Voucher",
      });
      if (!reason) return;
      rejReason = reason;
    } else {
      const result = await Swal.fire({
        title: `${actionTitle} - ${voucherNo}`,
        text: "Approving this voucher will immediately update the physical stock ledger in the ERP.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: confirmColor,
        confirmButtonText: "Yes, Approve & Update Stock",
      });
      if (!result.isConfirmed) return;
    }

    try {
      const payload = {
        id,
        status: newStatus,
        rej_reason: rejReason,
      };

      const response = await axios.post(
        `${V_URL}/prosafe/pro-stock-adjustment-approval`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
          },
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message ||
            `Adjustment ${isApprove ? "approved" : "rejected"} successfully`
        );
        fetchData();
      } else {
        toast.error(response.data.message || "Action failed");
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = (id, voucherNo) => {
    Swal.fire({
      title: `Delete Voucher - ${voucherNo}?`,
      text: "Are you sure? If this adjustment was already approved, the stock ledger will be rolled back.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete It!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.put(
            `${V_URL}/prosafe/pro-delete-stock-adjustment`,
            { id },
            {
              headers: {
                Authorization:
                  "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
              },
            }
          );
          if (response.data.success) {
            toast.success("Voucher deleted successfully");
            fetchData();
          } else {
            toast.error(response.data.message || "Failed to delete");
          }
        } catch (error) {
          console.error("Delete error:", error);
          toast.error(error.response?.data?.message || "Failed to delete");
        }
      }
    });
  };

  const handleDownloadExcel = async () => {
    try {
      const payload = {
        firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
        year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
        project_id: filter.project,
        status: filter.status,
        adjustment_type: filter.adjustment_type,
        start_date: filter.startDate,
        end_date: filter.endDate,
        search,
      };

      const res = await axios.post(
        `${V_URL}/prosafe/pro-stock-adjustment-xlsx-download`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Stock_Adjustments_${moment().format("YYYYMMDD")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to download Excel");
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const myurl = `${V_URL}/prosafe/download-stock-adjustment-slip/${id}`;
      const response = await axios.post(
        myurl,
        {},
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
          },
        }
      );

      if (
        response.data.success &&
        (response.data.data?.pdfUrl || response.data.data?.url)
      ) {
        window.open(
          response.data.data.pdfUrl || response.data.data.url,
          "_blank"
        );
        toast.success("Slip opened successfully");
      } else {
        toast.error(response.data.message || "Failed to generate PDF slip");
      }
    } catch (error) {
      console.error("Download PDF error:", error);
      toast.error(error.response?.data?.message || "Error generating slip");
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", link: "/main-store/user/dashboard" },
    { label: "Stock Adjustment" },
  ];

  const filterConfig = [
    {
      key: "project",
      label: "Project",
      type: "dropdown",
      value: filter.project,
      placeholder: "Select Project",
      searchable: true,
      options:
        projectData?.map((p) => ({
          label: `${p.name} ${p.project_no ? `(${p.project_no})` : ""}`,
          value: p._id,
        })) || [],
    },
    {
      key: "category",
      label: "Item Category",
      type: "dropdown",
      value: filter.category,
      placeholder: "Select Category",
      searchable: true,
      options:
        categoryData?.map((c) => ({
          label: c.name,
          value: c._id,
        })) || [],
    },
    {
      key: "item",
      label: "Item",
      type: "dropdown",
      value: filter.item,
      placeholder: "Select Item",
      searchable: true,
      options:
        itemData?.map((i) => ({
          label: `${i.code ? `${i.code} - ` : ""}${i.name}`,
          value: i._id,
        })) || [],
    },
    {
      key: "adjustment_type",
      label: "Adjustment Type",
      type: "dropdown",
      value: filter.adjustment_type,
      placeholder: "All Types",
      options: [
        { label: "+ Stock Inward (Gain / Surplus)", value: "PLUS" },
        { label: "- Stock Outward (Loss / Issues)", value: "MINUS" },
      ],
    },
    {
      key: "status",
      label: "Status",
      type: "dropdown",
      value: filter.status,
      placeholder: "All Statuses",
      options: [
        { label: "Pending", value: 1 },
        { label: "Approved", value: 3 },
        { label: "Rejected", value: 4 },
      ],
    },
    {
      key: "startDate",
      label: "Start Date",
      type: "date",
      value: filter.startDate,
      placeholder: "Select Start Date",
    },
    {
      key: "endDate",
      label: "End Date",
      type: "date",
      value: filter.endDate,
      placeholder: "Select End Date",
    },
  ];

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  <PageHeader
                    title="Stock Adjustment"
                    breadcrumbs={breadcrumbs}
                    onSearch={(val) => {
                      setSearch(val);
                      setCurrentPage(1);
                    }}
                    addButtonLink="/main-store/user/manage-stock-adjustment"
                    onRefresh={handleRefresh}
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    filterConfig={filterConfig}
                    limit={limit}
                    onLimitChange={(val) => setlimit(val)}
                    onDownload={handleDownloadExcel}
                  />

                  {isLoading ? (
                    <Loader />
                  ) : (
                    <div className="table-responsive">
                      <table className="table border-0 custom-table comman-table mb-0">
                        <thead>
                          <tr>
                            <th>Sr.</th>
                            <th>Date</th>
                            <th>Voucher No</th>
                            <th>Type</th>
                            <th>Project</th>
                            <th>Items</th>
                            <th>Total Qty</th>
                            <th>Total Amount (₹)</th>
                            <th>Reason</th>
                            <th>Status</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adjustmentsData.map((item, index) => {
                            const isPlus =
                              item.adjustment_type === "PLUS" ||
                              item.tagNumber === 18;
                            return (
                              <tr key={item._id || index}>
                                <td>{(currentPage - 1) * limit + index + 1}</td>
                                <td>
                                  {moment(item.trans_date).format("DD/MM/YYYY")}
                                </td>
                                <td>
                                  <Link
                                    to="/main-store/user/view-stock-adjustment"
                                    state={item}
                                    className="text-primary font-weight-bold"
                                  >
                                    {item.voucher_no || "-"}
                                  </Link>
                                </td>
                                <td>
                                  {isPlus ? (
                                    <span className="custom-badge status-green">
                                      + Inward
                                    </span>
                                  ) : (
                                    <span className="custom-badge status-pink">
                                      - Outward
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {item.projectName
                                    ? `${item.projectNo ? `(${item.projectNo}) ` : ""}${item.projectName}`
                                    : item.project_details?.name
                                    ? `${item.project_details?.project_no ? `(${item.project_details?.project_no}) ` : ""}${item.project_details?.name}`
                                    : "General"}
                                </td>
                                <td>
                                  <span className="custom-badge status-purple">
                                    {item.itemCount ||
                                      (item.items_details
                                        ? item.items_details.length
                                        : 0)}{" "}
                                    items
                                  </span>
                                </td>
                                <td className="font-weight-bold">
                                  {parseFloat(
                                    item.totalQuantity || 0
                                  ).toFixed(2)}
                                </td>
                                <td>
                                  ₹{parseFloat(item.totalAmount || 0).toFixed(2)}
                                </td>
                                <td>
                                  <span title={item.adjustment_reason || ""}>
                                    {(
                                      item.adjustment_reason ||
                                      "Physical Variance"
                                    ).slice(0, 30)}
                                    {(item.adjustment_reason || "").length > 30
                                      ? "..."
                                      : ""}
                                  </span>
                                </td>
                                <td>{getStatusLabel(item.status)}</td>
                                <td className="text-end">
                                  <div className="dropdown dropdown-action">
                                    <a
                                      href="#"
                                      className="action-icon dropdown-toggle"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <i className="fa fa-ellipsis-v"></i>
                                    </a>
                                    <div className="dropdown-menu dropdown-menu-end">
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          navigate(
                                            "/main-store/user/view-stock-adjustment",
                                            { state: item }
                                          )
                                        }
                                      >
                                        <i className="fa-solid fa-eye m-r-5"></i>{" "}
                                        View
                                      </button>

                                      {item.status !== 3 && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() =>
                                            navigate(
                                              "/main-store/user/manage-stock-adjustment",
                                              { state: item }
                                            )
                                          }
                                        >
                                          <i className="fa-solid fa-pen-to-square m-r-5"></i>{" "}
                                          Edit
                                        </button>
                                      )}

                                      {item.status === 1 && (
                                        <>
                                          <button
                                            type="button"
                                            className="dropdown-item text-success"
                                            onClick={() =>
                                              handleApproveReject(
                                                item._id,
                                                item.voucher_no,
                                                3
                                              )
                                            }
                                          >
                                            <i className="fa-solid fa-check-double m-r-5"></i>{" "}
                                            Approve Adjustment
                                          </button>
                                          <button
                                            type="button"
                                            className="dropdown-item text-danger"
                                            onClick={() =>
                                              handleApproveReject(
                                                item._id,
                                                item.voucher_no,
                                                4
                                              )
                                            }
                                          >
                                            <i className="fa-solid fa-ban m-r-5"></i>{" "}
                                            Reject Adjustment
                                          </button>
                                        </>
                                      )}

                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          handleDownloadPdf(item._id)
                                        }
                                      >
                                        <i className="fa-solid fa-download m-r-5"></i>{" "}
                                        Print Slip
                                      </button>

                                      {item.status === 1 && (
                                        <button
                                          type="button"
                                          className="dropdown-item text-danger"
                                          onClick={() =>
                                            handleDelete(
                                              item._id,
                                              item.voucher_no
                                            )
                                          }
                                        >
                                          <i className="fa fa-trash-alt m-r-5"></i>{" "}
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {adjustmentsData.length === 0 && (
                            <tr>
                              <td colSpan="999">
                                <div className="no-table-data">
                                  No Data Found!
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="row align-center mt-3 mb-2">
                    <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                      <div
                        className="dataTables_info"
                        id="DataTables_Table_0_info"
                        role="status"
                        aria-live="polite"
                      >
                        Showing {Math.min(limit, totalItems)} from {totalItems}{" "}
                        data
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                      <div
                        className="dataTables_paginate paging_simple_numbers"
                        id="DataTables_Table_0_paginate"
                      >
                        <Pagination
                          total={totalItems}
                          itemsPerPage={limit}
                          currentPage={currentPage}
                          onPageChange={(page) => setCurrentPage(page)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default StockAdjustmentList;
